<template>
  <button type="button" class="options-menu-tile" :class="{ 'options-menu-tile--pro': pro }" @click="$emit('select')">
    <span class="options-menu-tile__disc" aria-hidden="true">
      <span class="options-menu-tile__glyph" :data-icon="icon" :style="{ '--options-icon': iconMask }"></span>
    </span>
    <span class="options-menu-tile__label">{{ label }}</span>
    <span v-if="tag" class="options-menu-tile__tag">{{ tag }}</span>
  </button>
</template>

<script setup>
// One option in an OptionsMenuSection: a grey tile with its icon in a darker circle, as the apps
// draw it (OptionsSheetTile / MapOptionsCard). A PRO+ tool takes the blue glyph on blue at 20%
// instead of a badge, so the PRO+ tools read as a set (MapOptionsIconCircle).
import { computed } from 'vue'
import { optionsIconMask } from './optionsIcons'

const props = defineProps({
  // A key of OPTIONS_ICONS — the apps' options_ic_* glyph for this action.
  icon: { type: String, required: true },
  label: { type: String, required: true },
  pro: { type: Boolean, default: false },
  // A short marker after the label, e.g. "New" on a tool that has just changed.
  tag: { type: String, default: '' },
});
defineEmits(['select']);

const iconMask = computed(() => optionsIconMask(props.icon))
</script>

<style scoped>
/* background_card_grey, 10px corners, the apps' 50sdp minimum height. */
.options-menu-tile {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 52px;
  padding: 6px 10px;
  border: 0;
  border-radius: 10px;
  background: #22242a;
  color: #fff;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.25;
  text-align: left;
  /* The site's button style is nowrap; a label here wraps to a second line, as in the apps. */
  white-space: normal;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.options-menu-tile:hover {
  background: #2d2f35;
}

.options-menu-tile:focus-visible {
  outline: 2px solid var(--yellow, #fddf00);
  outline-offset: 2px;
}

/* background_card_grey_light disc; a 30sdp circle around a 17sdp glyph. */
.options-menu-tile__disc {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #2d2f35;
}

.options-menu-tile:hover .options-menu-tile__disc {
  background: #383a41;
}

.options-menu-tile__glyph {
  width: 17px;
  height: 17px;
  background-color: #fff;
  -webkit-mask: var(--options-icon) center / contain no-repeat;
  mask: var(--options-icon) center / contain no-repeat;
}

/* block_blue, and block_blue at 20% behind it. */
.options-menu-tile--pro .options-menu-tile__disc {
  background: rgba(44, 194, 227, 0.2);
}

.options-menu-tile--pro .options-menu-tile__glyph {
  background-color: #2cc2e3;
}

/* Two lines at most, as the apps allow. */
.options-menu-tile .options-menu-tile__label {
  display: -webkit-box;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow-wrap: break-word;
}

/* A corner badge rather than a word in the row, so it never squeezes the label into a mid-word break. */
.options-menu-tile__tag {
  position: absolute;
  top: -7px;
  right: 8px;
  padding: 1px 6px;
  line-height: 1.4;
  border-radius: 5px;
  background: var(--yellow, #fddf00);
  color: #17181d;
  font-size: 10px;
  font-weight: 700;
}
</style>
